// This component is used to be able to reconnect to the websocket.
// It is a top level component that will update the redux state to know
// if the user just reconnected.

// @flow
/* eslint-disable no-shadow */
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';

import useInterval from '../../hooks/useInterval';
import useNetwork from '../../hooks/useNetwork';
import { reconnectToWebSocket } from '../../actions';
import { setShouldShowReconnectingBanner, setWebsocketStatus } from '../../actions/session/socket';

const SocketReconnector = ({
  socketConnectionStatus,
  reconnectToWebSocket,
  setShouldShowReconnectingBanner,
  setWebsocketStatus,
  websocketStatus,
}: {
  socketConnectionStatus: string,
  reconnectToWebSocket: Function,
  setShouldShowReconnectingBanner: (shouldShow: boolean) => void,
  setWebsocketStatus: (status: 'online' | 'offline' | 'reconnecting') => void,
  websocketStatus: 'online' | 'offline' | 'reconnecting',
}) => {
  const isSocketActive = socketConnectionStatus === 'CONNECTED' || socketConnectionStatus === 'PENDING';
  const isOnline = useNetwork();
  const [triedReconnectingAt, setTriedReconnectingAt] = useState(0);

  useInterval(() => {
    // Every 5 seconds let's try reconnecting again
    // in case reconnect failed
    const timeDiff = triedReconnectingAt > 0 ? Date.now() - triedReconnectingAt : 0;
    if (!isSocketActive && (timeDiff === 0 || timeDiff > 5000)) {
      setTriedReconnectingAt(Date.now());
      reconnectToWebSocket();
      setWebsocketStatus('reconnecting');
      setShouldShowReconnectingBanner(true);
    }
  }, 500);

  useEffect(() => {
    const isReady = isOnline && isSocketActive;

    setShouldShowReconnectingBanner(!isReady);
    if (isReady) {
      if (websocketStatus !== 'online') {
        // We came back online, so we should
        // show the green banner for a little while.
        setShouldShowReconnectingBanner(true);
        setTimeout(() => {
          setShouldShowReconnectingBanner(false);
        }, 1500);
      }
      setWebsocketStatus('online');
      setTriedReconnectingAt(0);
    } else if (!isOnline) {
      setWebsocketStatus('offline');
    } else if (!isSocketActive) {
      setWebsocketStatus('reconnecting');
      setTriedReconnectingAt(Date.now());
    }
  }, [isOnline, isSocketActive]);

  return null;
};

const mapStateToProps = ({ scribeSession: { socketConnectionStatus, websocketStatus } }) => ({
  socketConnectionStatus,
  websocketStatus,
});

const mapDispatchToProps = {
  reconnectToWebSocket,
  setShouldShowReconnectingBanner,
  setWebsocketStatus,
};

export default connect(mapStateToProps, mapDispatchToProps)(SocketReconnector);
